/**
 * Tanıtım gönderimi (v0.1.135, step 3 of the pre-send plan Sercan approved on 1 Oct 2026). Before anything
 * is sent: for each campaign the five conditions a send needs (sending mailbox, inbox test, quality audit,
 * campaign approval, usable addresses) with where each stands, the draft counts, and the first wave's
 * mails, each in Turkish with the English that would go underneath. Read only.
 * v0.1.140: one wave block per campaign that has a mailbox, titled with the campaign and the region.
 */

import { VERDICT_REJECT_REASON_CODES, type VerdictRejectReasonCode } from '@twin/contracts';
import { useEffect, useState } from 'react';
import {
  ADDRESS_STATE_LABELS,
  AUDIT_FINDING_LABELS,
  AUDIT_STATUS_LABELS,
  CAMPAIGN_STATUS_LABELS,
  GATE_STATE_LABELS,
  MAIL_STATE_LABELS,
  OUTREACH_AUDIT_CODE_TR,
  OUTREACH_DRAFT_FLAG_TR,
  OUTREACH_REPLY_CLASS_TR,
  type OutreachAudit,
  type OutreachAuditItem,
  type OutreachCampaign,
  type OutreachGate,
  type OutreachReply,
  type OutreachView,
  type OutreachWave,
  type OutreachWaveMail,
  PROBE_RESULT_LABELS,
  REFUTATION_LABELS,
} from '../api/outreach.js';
import { OUTREACH_ARM_TR } from '../api/prospects.js';
import { REJECT_REASON_LABELS, type RejectReasonCode } from '../api/types.js';
import { useOutreach } from '../app/outreach.js';
import { Link } from '../app/router.js';
import { formatAge, formatClock, formatDayMonth, formatNumber } from '../lib/format.js';
import { POLICY_HOLD_MS, T4HoldButton } from './HoldButton.js';
import { Icon, type IconName } from './icons.js';
import { ChoiceChips } from './Sheet.js';
import { copyText } from './sheets.js';

const GATE_ICONS: Readonly<Record<OutreachGate['state'], IconName>> = {
  done: 'check',
  waiting: 'clock',
  failed: 'ban',
};

function GateRow({ gate }: { gate: OutreachGate }) {
  return (
    <li className={`ot-gate ot-gate--${gate.state}`}>
      <Icon name={GATE_ICONS[gate.state]} size={16} className="ot-gate__icon" />
      <span className="ot-gate__text">
        <span className="ot-gate__label">
          {gate.label}
          <span className="ot-gate__state">{GATE_STATE_LABELS[gate.state]}</span>
        </span>
        <span className="ot-gate__detail">{gate.detail}</span>
      </span>
    </li>
  );
}

function CampaignApproval({ campaign }: { campaign: OutreachCampaign }) {
  const { state, store } = useOutreach();
  const [error, setError] = useState<string | null>(null);
  if (campaign.status === 'active') {
    return (
      <p className="ot-approve ot-approve--done">
        Kampanya etkin: onayladığın mailler gönderim kapısından geçerek gider.
      </p>
    );
  }
  if (campaign.approval.blockedBy !== null && !campaign.approval.canApprove) {
    return <p className="ot-approve ink-2">Kampanya onayı için bekleyen şart: {campaign.approval.blockedBy}.</p>;
  }
  if (!campaign.approval.canApprove) return null;
  const busy = state.busy.includes(`campaign:${campaign.code}`);
  return (
    <div className="ot-approve">
      <T4HoldButton
        label="Kampanyayı onayla"
        sublabel={`Basılı tut · ${campaign.mailbox?.address ?? 'kutu'} · günde en çok ${campaign.mailbox?.dailyCap ?? 0} mail, kutudaki kampanyalar birlikte`}
        holdVerb="onaylamak"
        durationMs={POLICY_HOLD_MS}
        disabledReason={busy ? 'Gönderiliyor' : null}
        onComplete={() => {
          void store.approveCampaign(campaign.code).then(setError);
        }}
        onEarlyRelease={() => setError('Kampanyayı onaylamak için 1,5 saniye basılı tut.')}
      />
      <p className="ink-2 ot-approve__note">Onaylasan da mailler yine tek tek senin onayınla gider.</p>
      {error === null ? null : (
        <p className="danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function CampaignBlock({ campaign }: { campaign: OutreachCampaign }) {
  const done = campaign.gates.filter((gate) => gate.state === 'done').length;
  return (
    <section className="ot-campaign" aria-labelledby={`ot-${campaign.code}`}>
      <header className="ot-campaign__head">
        <h2 id={`ot-${campaign.code}`} className="ot-campaign__title">
          {campaign.name}
        </h2>
        <span className="ot-campaign__meta ink-2">
          {campaign.sender} · {CAMPAIGN_STATUS_LABELS[campaign.status] ?? campaign.status} · şartlar {done}/
          {campaign.gates.length}
        </span>
      </header>
      <ul className="ot-gates">
        {campaign.gates.map((gate) => (
          <GateRow key={gate.key} gate={gate} />
        ))}
      </ul>
      <dl className="ot-counts">
        <div>
          <dt>İlk mail</dt>
          <dd>{formatNumber(campaign.drafts.firstMails)}</dd>
        </div>
        <div>
          <dt>Adresi hazır</dt>
          <dd>{formatNumber(campaign.drafts.ready)}</dd>
        </div>
        <div>
          <dt>Adres kontrolü bekliyor</dt>
          <dd>{formatNumber(campaign.drafts.waitingAddress)}</dd>
        </div>
        <div>
          <dt>Takip maili (14 gün sonra)</dt>
          <dd>{formatNumber(campaign.drafts.secondMails)}</dd>
        </div>
        <div>
          <dt>Engelli</dt>
          <dd>{formatNumber(campaign.drafts.blocked)}</dd>
        </div>
      </dl>
      <CampaignApproval campaign={campaign} />
    </section>
  );
}

/** Onayla, or Reddet with a reason, on one wave mail or one answer to a reply (v0.1.143). */
function MailDecision({ mail }: { mail: Pick<OutreachWaveMail, 'messageId' | 'canDecide' | 'company'> }) {
  const { state, store } = useOutreach();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState<VerdictRejectReasonCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const busy = state.busy.includes(`message:${mail.messageId}`);
  if (!mail.canDecide) return null;
  return (
    <div className="ot-decision">
      {rejecting ? (
        <>
          <ChoiceChips
            label={`${mail.company}: ret gerekçesi`}
            options={VERDICT_REJECT_REASON_CODES}
            value={reason}
            onChange={setReason}
            labels={REJECT_REASON_LABELS}
          />
          <div className="ot-finding__actions">
            <button
              type="button"
              className="btn btn--small btn--danger-outline"
              disabled={busy || reason === null}
              onClick={() => {
                if (reason === null) return;
                void store.decide(mail.messageId, { decision: 'reject', rejectReasonCode: reason }).then(setError);
              }}
            >
              Reddet
            </button>
            <button type="button" className="btn btn--small btn--secondary" onClick={() => setRejecting(false)}>
              Vazgeç
            </button>
          </div>
        </>
      ) : (
        <div className="ot-finding__actions">
          <button
            type="button"
            className="btn btn--small btn--primary"
            disabled={busy}
            onClick={() => void store.decide(mail.messageId, { decision: 'approve' }).then(setError)}
          >
            Onayla
          </button>
          <button
            type="button"
            className="btn btn--small btn--secondary"
            disabled={busy}
            onClick={() => setRejecting(true)}
          >
            Reddet
          </button>
        </div>
      )}
      {error === null ? null : (
        <p className="danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** v0.1.142: the 14-day rule in words; null when nothing holds the mail. */
function brandWaitText(mail: OutreachWaveMail): string | null {
  const wait = mail.brandWait;
  if (wait === null) return null;
  return wait.until === null
    ? `İlk mail (${wait.otherCampaign}) onaylandı, henüz gitmedi; bu takip maili o gittikten 14 gün sonra onaylanabilir.`
    : `Bu firmaya ilk mail (${wait.otherCampaign}) gitti; bu takip maili ${formatDayMonth(wait.until)} tarihinden sonra onaylanabilir.`;
}

function mailStateText(mail: OutreachWaveMail): string | null {
  if (mail.reply !== null) {
    const kind = mail.reply.classification === null ? 'okunacak' : OUTREACH_REPLY_CLASS_TR[mail.reply.classification];
    return `Cevap geldi · ${kind}`;
  }
  if (mail.decision.state === 'open' && mail.brandWait !== null) {
    return mail.brandWait.until === null ? '14 gün kuralı' : `14 gün kuralı · ${formatDayMonth(mail.brandWait.until)}`;
  }
  const label = MAIL_STATE_LABELS[mail.decision.state] ?? '';
  if (label === '') return null;
  const at = mail.decision.at === null ? '' : ` · ${formatClock(mail.decision.at)}`;
  const reason =
    mail.decision.detail === null
      ? ''
      : `: ${mail.decision.state === 'rejected' ? (REJECT_REASON_LABELS[mail.decision.detail as RejectReasonCode] ?? mail.decision.detail) : mail.decision.detail}`;
  return `${label}${at}${reason}`;
}

function MailCard({ mail }: { mail: OutreachWaveMail }) {
  const [open, setOpen] = useState(false);
  const [english, setEnglish] = useState(false);
  return (
    <li className="outbox-card ot-mail">
      <button type="button" className="outbox-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="outbox-card__title">
          <span className="outbox-card__subject">{mail.company}</span>
          <span className="outbox-card__meta ink-2">
            {mail.subjectTr ?? mail.subject ?? 'konu yok'}
            {mail.domain === null ? '' : ` · ${mail.domain}`}
          </span>
        </span>
        <span
          className={
            mailStateText(mail) === null
              ? `outbox-card__state ot-address ot-address--${mail.addressState}`
              : `outbox-card__state ot-mailstate ot-mailstate--${mail.decision.state}`
          }
        >
          {mailStateText(mail) ?? ADDRESS_STATE_LABELS[mail.addressState]}
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} className="ink-3" />
      </button>
      {open ? (
        <div className="outbox-card__body">
          <dl className="ot-facts">
            <div>
              <dt>Kime</dt>
              <dd>{mail.recipient ?? 'adres yok'}</dd>
            </div>
            <div>
              <dt>Adresi gördüğümüz sayfa</dt>
              <dd>
                {mail.foundOn === null ? (
                  'kayıtlı değil'
                ) : (
                  <a href={mail.foundOn} target="_blank" rel="noreferrer noopener">
                    {mail.foundOn}
                  </a>
                )}
              </dd>
            </div>
            {mail.arm === null ? null : (
              <div>
                <dt>Sıra</dt>
                <dd>{OUTREACH_ARM_TR[mail.arm] ?? mail.arm}</dd>
              </div>
            )}
            <div>
              <dt>Kalite denetimi</dt>
              <dd>{mail.audited ? 'temiz' : 'henüz denetlenmedi'}</dd>
            </div>
            {brandWaitText(mail) === null ? null : (
              <div>
                <dt>14 gün kuralı</dt>
                <dd>{brandWaitText(mail)}</dd>
              </div>
            )}
          </dl>
          <section className="outbox-block" aria-label="Türkçe okunuşu">
            <h3 className="lr-sub">Türkçesi</h3>
            {mail.subjectTr ? <p className="ot-mail__subject">{mail.subjectTr}</p> : null}
            <p className="outbox-block__tr">{mail.bodyTr ?? 'Türkçe okunuş yok.'}</p>
          </section>
          <section className="outbox-block" aria-label="Gidecek İngilizce metin">
            <button
              type="button"
              className="text-btn ot-mail__toggle"
              aria-expanded={english}
              onClick={() => setEnglish(!english)}
            >
              {english ? 'İngilizce metni gizle' : 'Gidecek İngilizce metni göster'}
            </button>
            {english ? (
              <>
                {mail.subject ? (
                  <p lang="en" className="ot-mail__subject">
                    {mail.subject}
                  </p>
                ) : null}
                <p lang="en" className="outbox-block__en outbox-block__en--reply">
                  {mail.body}
                </p>
              </>
            ) : null}
          </section>
          <MailDecision mail={mail} />
        </div>
      ) : null}
    </li>
  );
}

function FindingCard({ audit, item }: { audit: OutreachAudit; item: OutreachAuditItem }) {
  const { state, store } = useOutreach();
  const [error, setError] = useState<string | null>(null);
  const busy = state.busy.includes(`${audit.auditId}:${item.itemNo}`);
  const decide = async (decision: 'upheld' | 'overturned') => {
    setError(await store.refute(audit.auditId, item.itemNo, decision));
  };
  const codes = item.codes.map((code) => OUTREACH_AUDIT_CODE_TR[code] ?? code).join(', ');
  return (
    <li className={`ot-finding ot-finding--${item.finding}`}>
      <div className="ot-finding__head">
        <span className="ot-finding__company">{item.company}</span>
        <span className="ot-finding__kind">{AUDIT_FINDING_LABELS[item.finding]}</span>
      </div>
      {codes === '' ? null : <p className="ot-finding__codes ink-2">{codes}</p>}
      {item.note === null ? null : <p className="ot-finding__note">{item.note}</p>}
      {item.refutation === 'pending' && audit.status === 'running' ? (
        <div className="ot-finding__actions">
          <button
            type="button"
            className="btn btn--small btn--danger-outline"
            disabled={busy}
            onClick={() => void decide('upheld')}
          >
            Denetçi haklı, çıkar
          </button>
          <button
            type="button"
            className="btn btn--small btn--secondary"
            disabled={busy}
            onClick={() => void decide('overturned')}
          >
            Denetçi yanılıyor, kalsın
          </button>
        </div>
      ) : (
        <p className="ot-finding__done ink-2">{REFUTATION_LABELS[item.refutation]}</p>
      )}
      {error === null ? null : (
        <p className="danger" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

function AuditBlock({ audit, campaignName }: { audit: OutreachAudit | null; campaignName: string }) {
  if (audit === null) {
    return (
      <p className="ot-audit ink-2">
        Kalite denetimi en az 20 firmanın adresi kontrolden geçince başlar: Denetçi her firmanın kendi sayfalarını okur.
      </p>
    );
  }
  const waiting = audit.findings.filter((item) => item.refutation === 'pending').length;
  return (
    <section className="ot-audit" aria-label={`Kalite denetimi · ${campaignName}`}>
      <h3 className="lr-sub">Kalite denetimi · {AUDIT_STATUS_LABELS[audit.status] ?? audit.status}</h3>
      <p className="ink-2">
        {audit.size} firmadan {audit.read} tanesi okundu: {audit.ok} temiz, {audit.findings.length} itiraz
        {waiting > 0 ? `, ${waiting} tanesi kararını bekliyor` : ''}.
      </p>
      {audit.findings.length === 0 ? null : (
        <ul className="ot-findings">
          {audit.findings.map((item) => (
            <FindingCard key={item.itemNo} audit={audit} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

function WaveBlock({ wave }: { wave: OutreachWave }) {
  const ready = wave.mails.filter((mail) => mail.addressState === 'usable').length;
  const headingId = `ot-wave-${wave.campaignCode}`;
  return (
    <section className="ot-wave" aria-labelledby={headingId}>
      <header className="ot-campaign__head">
        <h2 id={headingId} className="ot-campaign__title">
          İlk dalga · {wave.campaignName} · {wave.regionName}
        </h2>
        <span className="ot-campaign__meta ink-2">
          hedef {wave.target} firma · {formatNumber(wave.candidates)} ilk mail hazır · adresi kontrolden geçen {ready}
        </span>
      </header>
      <AuditBlock audit={wave.audit} campaignName={wave.campaignName} />
      <p className="ot-wave__note ink-2">
        Her mail önce Türkçesiyle görünür; gidecek İngilizce metin altında. Onay ve gönderim, şartlar tamamlanınca bu
        listeden olacak.
      </p>
      {wave.mails.length === 0 ? (
        <p className="ink-2">Bu bölgede henüz ilk mail yok.</p>
      ) : (
        <ul className="outbox-list">
          {wave.mails.map((mail) => (
            <MailCard key={mail.messageId} mail={mail} />
          ))}
        </ul>
      )}
    </section>
  );
}

const RESULT_TARGETS = 'Hedef: cevap oranı %5 ve üstü, geri dönen %2 altı, şikâyet sıfır.';

function ResultsBlock({ results }: { results: OutreachView['results'] }) {
  if (results.sent === 0) return null;
  const rate = (value: number | null) => (value === null ? '-' : `%${String(value).replace('.', ',')}`);
  return (
    <section className="ot-results" aria-label="Sonuçlar">
      <h2 className="ot-campaign__title">Sonuçlar</h2>
      <dl className="ot-counts">
        <div>
          <dt>Giden</dt>
          <dd>{formatNumber(results.sent)}</dd>
        </div>
        <div>
          <dt>Geri dönen</dt>
          <dd>
            {formatNumber(results.bounced)} · {rate(results.bounceRate)}
          </dd>
        </div>
        <div>
          <dt>Cevap veren firma</dt>
          <dd>
            {formatNumber(results.replied)} · {rate(results.replyRate)}
          </dd>
        </div>
        <div>
          <dt>İlgilenen</dt>
          <dd>{formatNumber(results.interested)}</dd>
        </div>
        <div>
          <dt>İlgilenmeyen</dt>
          <dd>{formatNumber(results.negative)}</dd>
        </div>
        <div>
          <dt>Listeden çıkan</dt>
          <dd>{formatNumber(results.unsubscribed)}</dd>
        </div>
      </dl>
      <p className="ink-2 ot-results__targets">{RESULT_TARGETS}</p>
    </section>
  );
}

/** v0.1.143: the Temsilci's reading of a reply (Turkish copy, note, flags, open questions) and its answer. */
function ReplyDraft({ reply }: { reply: OutreachReply }) {
  const [english, setEnglish] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const draft = reply.draft;
  if (draft === null) return null;
  const answer = draft.answer;
  const flags = draft.flags.map((flag) => OUTREACH_DRAFT_FLAG_TR[flag] ?? flag);
  const state = answer === null ? '' : (MAIL_STATE_LABELS[answer.decision.state] ?? '');
  return (
    <div className="ot-reply__draft">
      {draft.theirTextTr === null ? null : (
        <section className="outbox-block" aria-label="Cevabın Türkçesi">
          <h3 className="lr-sub">Türkçesi</h3>
          <p className="outbox-block__tr">{draft.theirTextTr}</p>
        </section>
      )}
      {flags.length === 0 ? null : <p className="ot-reply__flags">{flags.join(' · ')}</p>}
      {draft.notesTr === null ? null : <p className="ink-2 ot-reply__note">Temsilci: {draft.notesTr}</p>}
      {draft.questionsTr.length === 0 ? null : (
        <ul className="ot-reply__questions" aria-label="Temsilci'nin cevaplayamadığı sorular">
          {draft.questionsTr.map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
      )}
      {answer === null ? null : (
        <section className="outbox-block" aria-label="Temsilci'nin cevap taslağı">
          <h3 className="lr-sub">
            Cevap taslağı{answer.recipient === null ? '' : ` · ${answer.recipient}`}
            {state === '' ? '' : ` · ${state}`}
          </h3>
          <p className="outbox-block__tr">{answer.bodyTr ?? 'Türkçe okunuş yok.'}</p>
          <button
            type="button"
            className="text-btn ot-mail__toggle"
            aria-expanded={english}
            onClick={() => setEnglish(!english)}
          >
            {english ? 'İngilizce metni gizle' : 'Gidecek İngilizce metni göster'}
          </button>
          {english ? (
            <>
              {answer.subject === null ? null : (
                <p lang="en" className="ot-mail__subject">
                  {answer.subject}
                </p>
              )}
              <p lang="en" className="outbox-block__en outbox-block__en--reply">
                {answer.body}
              </p>
            </>
          ) : null}
          <div className="ot-finding__actions">
            <button
              type="button"
              className="btn btn--small btn--secondary"
              onClick={() => {
                void copyText(answer.body).then((ok) =>
                  setCopied(ok ? 'İngilizce metin kopyalandı; kendi kutundan gönderebilirsin.' : 'Kopyalanamadı.'),
                );
              }}
            >
              İngilizce metni kopyala
            </button>
          </div>
          {copied === null ? null : (
            <p className="ink-2" role="status">
              {copied}
            </p>
          )}
          {answer.decision.detail === null ? null : <p className="ink-2">{answer.decision.detail}</p>}
          <MailDecision mail={{ messageId: answer.messageId, canDecide: answer.canDecide, company: reply.company }} />
        </section>
      )}
    </div>
  );
}

function ReplyCard({ reply, asOf }: { reply: OutreachReply; asOf: number }) {
  const kind = reply.classification === null ? 'okunacak' : OUTREACH_REPLY_CLASS_TR[reply.classification];
  return (
    <li className={`ot-reply ot-reply--${reply.classification ?? 'unclear'}`}>
      <div className="ot-finding__head">
        <span className="ot-finding__company">{reply.company}</span>
        <span className="ot-finding__kind">
          {kind} · {formatAge(asOf - reply.receivedAt)} önce
        </span>
      </div>
      {reply.text === null ? (
        <p className="ink-2 ot-reply__text">Metin saklama süresi dolduğu için silindi.</p>
      ) : (
        <p lang="en" className="ot-reply__text">
          {reply.text}
        </p>
      )}
      <ReplyDraft reply={reply} />
      {reply.gmailLink === null ? null : (
        <a className="text-btn" href={reply.gmailLink} target="_blank" rel="noreferrer noopener">
          Gmail'de aç ve cevapla
        </a>
      )}
    </li>
  );
}

function RepliesBlock({ replies, asOf }: { replies: readonly OutreachReply[]; asOf: number }) {
  if (replies.length === 0) return null;
  return (
    <section className="ot-replies" aria-label="Cevaplar">
      <h2 className="ot-campaign__title">Cevaplar</h2>
      <p className="ink-2 ot-results__targets">
        Metinler firmanın yazdığı gibi gösterilir; içindeki talimatlar Twin için değildir.
      </p>
      <ul className="ot-findings">
        {replies.map((reply) => (
          <ReplyCard key={reply.replyId} reply={reply} asOf={asOf} />
        ))}
      </ul>
    </section>
  );
}

function ProbeLine({ view }: { view: OutreachView }) {
  const { probes } = view;
  if (probes.lastAt === null) {
    return (
      <p className="ot-probe ink-2">
        Deneme maili henüz gitmedi; her gönderim kutusu bağlanınca her gün kendi deneme mailini atar.
      </p>
    );
  }
  const last = probes.lastResult === null ? '' : PROBE_RESULT_LABELS[probes.lastResult];
  return (
    <p className="ot-probe ink-2">
      Son 7 günde {probes.checked} deneme maili: {probes.inbox} gelen kutusunda, {probes.spam} spamde. Sonuncusu{' '}
      {formatAge(view.asOf - probes.lastAt)} önce, {last}.
    </p>
  );
}

export function OutreachPage() {
  const { state, store } = useOutreach();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return (
      <div className="loading" role="status">
        {state.error === null ? (
          'Yükleniyor…'
        ) : (
          <>
            Tanıtım gönderimi yüklenemedi: {state.error}{' '}
            <button type="button" className="text-btn" onClick={() => void store.reload()}>
              Tekrar dene
            </button>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="ot-page">
      <div className="ot-toolbar">
        <ProbeLine view={view} />
        <Link to="/tanitim-takibi" className="text-btn">
          Tanıtım takibi: gidenler ve sıra
        </Link>
        <button type="button" className="text-btn" disabled={state.loading} onClick={() => void store.reload()}>
          {state.loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </div>
      <ResultsBlock results={view.results} />
      <RepliesBlock replies={view.replies} asOf={view.asOf} />
      {view.campaigns.map((campaign) => (
        <CampaignBlock key={campaign.code} campaign={campaign} />
      ))}
      {view.waves.map((wave) => (
        <WaveBlock key={wave.campaignCode} wave={wave} />
      ))}
      {view.notes.length === 0 ? null : (
        <ul className="ot-notes ink-2">
          {view.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
